import React from "react";
import {Box, Grid, makeStyles, Typography} from '@material-ui/core';
import styled from "styled-components";
import {useRanger} from "react-ranger";
import {
  arrangeProductsWithCards,
  generateSplitParts,
  getCheckPaymentPs,
  getPriceSelectorAmount,
  prepareProductForResult
} from '../../utils/productUtils';

import {ProductSellType, ProductType, TravelCareType,} from '../../api/enums';
import {CustomSwitch} from '../Switch/Switch';

const useStyles = makeStyles( () => ({
  customFontSize: {
    fontSize: '14px'
  },
  splitsWrapper: {
    position: 'relative',
    display: 'block',
    minHeight: '100%',
    top: '-5px'
  },

  splitsItem: {
    position: 'absolute',
    height: '16px',
    borderRight: '1px solid #ccc',
    left: 0,
    top: '-5px',
    bottom: '-20px',
    '&:nth-child(2n)': {
      top: '-20px',
      height: '32px',
    }
  },

  splitsItemValue: {
    position: 'absolute',
    right: 0,
    top: '-10px',
    fontSize: '12px',
    width:  'auto',
    textAlign: 'center',

  },

  splitsItemValueText: {
    position: 'absolute',
    left: '-30px',
    top: -4,
    textAlign: 'center',
    width: '60px',
    paddingBottom: '2px'
  }

}) )

export const Track = styled( 'div' )`
  display: inline-block;
  border-radius: 33px;
  height: 8px;
  width: 100%;
`;

export const Segment = styled("div")`
  background: ${(props) =>
    props.index === 0
      ? "#00C908"
      : props.index === 1
      ? "#9D1D5A"
      : props.index === 2
      ? "#FF6D4D"
      : "#4A94EC"};
  height: 100%;
`;

export const SegmentValue = styled(Box)`
  cursor: default;
  background: white;
  border-radius: 10px;
  height: 62px;
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) =>
    props.index === 0
      ? "#00C908"
      : props.index === 1
      ? "#9D1D5A"
      : props.index === 2
      ? "#FF6D4D"
      : "#4A94EC"};
`;

export const RangeResults = styled(Grid)`
  width: 100%;
`;

export const Handle = styled("div")`
  background: white;
  width: 32px;
  height: 32px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  border-radius: 26px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const getCardStrip = ( cardNumber ) => {
  return cardNumber.slice(
    cardNumber.length - 5
  )
}

const splitToNChunks = ( array, n ) => {
  const result = []
  for (let i = n; i > 0; i--) {
    result.push( array.splice( 0, Math.ceil( array.length / i ) ) )
  }

  return result
}

const formatMoney = ( value ) => {
  if (typeof value === 'string') {
    return value
  } else if (typeof value === 'number') {
    return Math.max( 0, value ).toFixed( 2 )
  } else {
    return value
  }
}

function seedProducts (priceQuoteProducts=[], tp_type=TravelCareType.Decline, tpAmount=0, is_tips =false,  tipsAmount=0) {

  const tips_id = priceQuoteProducts.find(product => product.type === ProductType.Tips)?.id
  const tp_id = priceQuoteProducts.find(product => product.type === ProductType.Insurance)?.id

  const result = [...priceQuoteProducts].filter( product => {
    return ![ProductType.Insurance, ProductType.Tips].includes(product.type)
  })

  if (tp_type === TravelCareType.Apply) {
    result.push( {
      id: tp_id || 'insurance_1',
      airline: 0,
      pre_ticket_id: null,
      in_house: Number(tpAmount),
      type: ProductType.Insurance,
      sale_type: ProductSellType.Published,
      card_id: null,
    } )
  }

  if (is_tips && tipsAmount > 0) {
    result.push( {
      id: tips_id || 'tips_1',
      airline: 0,
      pre_ticket_id: null,
      in_house: Number(tipsAmount),
      type: ProductType.Tips,
      sale_type: ProductSellType.Published,
      card_id: null,
    } )
  }


  return result
}

const RangeSlider = (
{
  max,
  cards,
  setValue,
  initialAmmounts,
  selectedFlight,
  manualSplitValue,
  tipsValue,
  acceptTipsValue,
  travelCareValue,
  isForceManualSplitValue,
  previewMode
}
) => {
  const classes = useStyles();

  const oldMode = !selectedFlight?.products?.length


  const productsValue = seedProducts(selectedFlight?.products, travelCareValue, selectedFlight?.travelCarePrice, acceptTipsValue, tipsValue)
  const [values, setValues] = React.useState([]);

  const arranged_products = arrangeProductsWithCards( cards, productsValue )

  const splitParts = generateSplitParts( arranged_products )
  const splitPartsForRange = splitParts.map((value => {
    return {
      value,
      width: value / Number(max) * 100
    }
  }))


  const updateValues = (newValues) => {
    setValues( newValues );
    updateCardAmmounts()
  }

  React.useEffect(() => {
    let newValues = []

    if (oldMode || manualSplitValue) {
      newValues = new Array( cards.length - 1 )
        .fill( null )
        .map( ( _, idx ) => parseFloat(((Number(max) / cards.length) * (idx + 1)).toFixed( 2 ) ));
    } else {

      const chunks = splitToNChunks( splitParts, cards.length - 1 )

      newValues = new Array( cards.length - 1)
        .fill( null )
        .map( ( _, idx ) => parseFloat(getPriceSelectorAmount( chunks[idx] , idx)).toFixed( 2 ) );

    }

    if (oldMode && initialAmmounts) {
      let current = 0
      for (let [idx, amount] of initialAmmounts.entries()) {
        current += amount
        if (newValues[idx]) {
          newValues[idx] = current
        }
      }
    }

    updateValues( newValues );
  }, [cards, max, travelCareValue, selectedFlight.travelCarePrice, acceptTipsValue, tipsValue]);

  const handleChange = ( v) => {
    updateValues(v)
  };

  const updateCardAmmounts = () => {
    // logic for cardsAmmout

    const lastCard = +((Number(max) - values[values.length - 1]).toFixed( 2 ));
    const newArrFromValues = values.map( ( item, idx ) =>
      idx === 0 ? item : item - values[idx - 1]
    );
    const cardAmmounts = [...newArrFromValues, lastCard].map(Number)

    if (!oldMode) {
      updateProducts( cardAmmounts )
    }
    setValue( 'cardsAmmount', cardAmmounts );
  }

  const updateProducts = ( cardAmmounts ) => {
    const arranged_products = arrangeProductsWithCards( cards, productsValue )

    const products = arranged_products.map( product => {
      return {
        ...product,
        card_id: null
      }
    } )

    if (!manualSplitValue) {
      for (const [index, card] of cards.entries()) {
        let card_amount = Number( cardAmmounts[index] )
        for (const [p_index, product] of products.entries()) {
          if (card_amount === 0) {
            break
          }
          const sell_price = Number(product.in_house) + Number(product.airline)
          if (!product?.card_id && card_amount - sell_price >= 0) {
            card_amount = card_amount - sell_price
            products[p_index].card_id = card.card_id
          }
        }
      }
    }

    const resultProducts = products.map( product => prepareProductForResult( product ) )

    setValue( 'productsResult', resultProducts );
  }

  const { getTrackProps, segments, handles } = useRanger({
    min: 0,
    max: Number(max),
    stepSize: 1,
    values,
    onDrag: updateValues,
    onChange: handleChange,
    steps: oldMode || manualSplitValue ? null : splitParts
  } );

  return (
    <>
      <Box mb={2}>
        <Typography variant="h2">Split your payment as you like</Typography>
      </Box>

      { (!oldMode || previewMode) &&
        <Box mt={2} mb={6}>
          <CustomSwitch
            name="manualSplit"
            value={manualSplitValue}
            defaultValue={false}
            disabled={isForceManualSplitValue}
            label={
              <Box>
                <Box mb={1}>
                  <Typography variant="h4">
                    I want manual split
                  </Typography>
                </Box>
                {(getCheckPaymentPs( selectedFlight ) > 0) &&
                  <Typography className={classes.customFontSize}>
                    If you choose to split the payment in a different manner, an additional charge
                    of {(getCheckPaymentPs( selectedFlight ) * 100).toFixed( 1 )}% may be
                    applied.
                  </Typography>
                }
                {(getCheckPaymentPs( selectedFlight ) === 0) &&
                  <Typography className={classes.customFontSize}>
                    You can choose to split the payment in a different manner.
                  </Typography>
                }

              </Box>
            }
          />
        </Box>
      }


      <Box mb={4}>
        {!oldMode && !manualSplitValue &&
          <div className={classes.splitsWrapper}>
            {splitPartsForRange?.map( ( item ) => (
              <div className={classes.splitsItem} style={{width: item.width + '%'}} key={`${item.width}--${item.value}`}>
                <div className={classes.splitsItemValue}>
                  <div className={classes.splitsItemValueText}>{item.value.toFixed(2)}</div>
                </div>
              </div>
            ) )}
          </div>
        }

        <Track {...getTrackProps()}>
          {segments?.map( ( {getSegmentProps}, i ) => (
            <Segment {...getSegmentProps()} index={i}/>
          ) )}
          {handles?.map( ( {getHandleProps} ) => (
            <Handle
              {...getHandleProps( {
                style: {
                  appearance: 'none',
                  outline: 'none'
                }
              })}
            />
          ))}
        </Track>
      </Box>
      <RangeResults container spacing={1}>
        {values?.map((val, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <SegmentValue
              index={idx}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Box>
                <Typography variant="caption">
                  {`•••• ${
                    cards[idx]?.cardNumber
                      ? cards[idx]?.cardNumber.slice(
                          cards[idx]?.cardNumber?.length - 5
                        )
                      : "••••"
                  }`}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  {`$ ${
                    idx === 0
                      ? formatMoney( val )
                      : formatMoney( val - values[idx - 1] )
                  }`}
                </Typography>
              </Box>
            </SegmentValue>
          </Grid>
        ))}
        <Grid item xs={6} sm={3} key={values.length - 1}>
          <SegmentValue
            index={values.length}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Box>
              <Typography variant="caption">
                {`•••• ${
                  cards[values.length]?.cardNumber
                    ? getCardStrip( cards[values.length]?.cardNumber )
                    : '•••• '
                }`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2">
                {`$ ${formatMoney( Number(max) - values[values.length - 1] )}`}
              </Typography>
            </Box>
          </SegmentValue>
        </Grid>
      </RangeResults>
    </>
  );
};

export default RangeSlider;
